<template>
  <div class="waajo-license-page">
    <header class="waajo-license-header">
      <router-link to="/" class="waajo-license-brand" aria-label="Waajo">
        <img v-if="logoText" :src="logoText" :alt="appTitle || 'Waajo'" />
        <span v-else>Waajo</span>
      </router-link>

      <div class="waajo-license-account">
        <span class="waajo-license-avatar" aria-hidden="true">
          {{ accountInitials }}
        </span>
        <span class="waajo-license-account-copy">
          <strong>{{ accountName }}</strong>
          <small>{{ accountEmail }}</small>
        </span>
      </div>
    </header>

    <impersonation-banner inline></impersonation-banner>

    <main class="waajo-license-main">
      <div class="waajo-license-heading">
        <span class="waajo-license-heading-icon" aria-hidden="true">
          <i class="fa fa-whatsapp"></i>
        </span>
        <span class="waajo-license-eyebrow">Pilih Lisensi</span>
        <h1>Pilih akun WhatsApp untuk melanjutkan</h1>
        <p>
          Pilih lisensi untuk membuka dashboard yang sesuai dengan akun
          WhatsApp Anda.
        </p>
      </div>

      <div v-if="loading" class="waajo-license-loading" role="status" aria-live="polite">
        <div class="waajo-license-card waajo-license-skeleton" v-for="index in 3" :key="index">
          <span class="waajo-skeleton-icon"></span>
          <span class="waajo-skeleton-title"></span>
          <span class="waajo-skeleton-subtitle"></span>
          <span class="waajo-skeleton-pill"></span>
          <span class="waajo-skeleton-action"></span>
        </div>
        <p v-if="autoSelecting" class="waajo-license-loading-text">
          Menyiapkan dashboard untuk lisensi Anda...
        </p>
      </div>

      <div v-else-if="errorMessage && !rows.length" class="waajo-license-state">
        <span class="waajo-license-state-icon is-danger" aria-hidden="true">
          <i class="fa fa-exclamation-circle"></i>
        </span>
        <h2>Lisensi belum dapat dimuat</h2>
        <p>{{ errorMessage }}</p>
        <button type="button" class="waajo-license-primary" @click="loadLicenses">
          Coba Lagi
        </button>
      </div>

      <div v-else-if="!rows.length" class="waajo-license-state">
        <span class="waajo-license-state-icon" aria-hidden="true">
          <i class="fa fa-whatsapp"></i>
        </span>
        <h2>Belum ada lisensi WhatsApp</h2>
        <p>
          Aktifkan voucher yang sudah Anda miliki atau pilih paket untuk mulai
          menggunakan Waajo.
        </p>
        <div class="waajo-license-state-actions">
          <router-link to="/order/active_voucher" class="waajo-license-primary">
            Aktifkan Voucher
          </router-link>
          <router-link to="/subscriptions" class="waajo-license-secondary">
            Beli Lisensi
          </router-link>
        </div>
      </div>

      <template v-else>
        <div v-if="errorMessage" class="waajo-license-error" role="alert">
          <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
          <span>{{ errorMessage }}</span>
        </div>

        <div class="waajo-license-grid">
          <article
            class="waajo-license-card"
            v-for="row in rows"
            :key="row.id"
            :class="{ 'is-selecting': selectingId === row.id }"
          >
            <div class="waajo-license-card-top">
              <span class="waajo-license-wa-icon" aria-hidden="true">
                <i class="fa fa-whatsapp"></i>
              </span>
              <div class="waajo-license-card-title">
                <h2>{{ row.license_phone_number || "Nomor WhatsApp" }}</h2>
                <p>{{ row.license_name || "Lisensi WhatsApp" }}</p>
              </div>
              <router-link
                :to="'/license/info?id=' + row.id"
                class="waajo-license-detail"
                title="Lihat detail lisensi"
                aria-label="Lihat detail lisensi"
              >
                <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
              </router-link>
            </div>

            <div class="waajo-license-status-row">
              <span class="waajo-license-status" :class="statusFor(row).className">
                <i class="fa fa-circle" aria-hidden="true"></i>
                {{ statusFor(row).label }}
              </span>
              <span class="waajo-license-online" :class="{ 'is-online': Number(row.is_online_status) === 1 }">
                <i class="fa fa-signal" aria-hidden="true"></i>
                {{ Number(row.is_online_status) === 1 ? "Online" : "Offline" }}
              </span>
            </div>

            <p class="waajo-license-expiry">{{ expiryLabel(row) }}</p>

            <button
              type="button"
              class="waajo-license-primary waajo-license-enter"
              :disabled="Boolean(selectingId)"
              @click="selectLicense(row)"
            >
              <span v-if="selectingId === row.id" class="waajo-spinner" aria-hidden="true"></span>
              <i v-else class="fa fa-arrow-right" aria-hidden="true"></i>
              {{ selectingId === row.id ? "Menyiapkan..." : "Masuk ke Dashboard" }}
            </button>
          </article>
        </div>
      </template>

      <p class="waajo-license-help">
        Butuh bantuan mengelola akun?
        <router-link to="/license/list">Buka daftar lisensi</router-link>
      </p>
    </main>

    <footer class="waajo-license-footer">
      <span>Waajo — platform WhatsApp untuk bisnis.</span>
      <router-link to="/terms">Ketentuan Layanan</router-link>
    </footer>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  formatLicenseDate,
  getLicenseExpiryInfo
} from "@/utils/licenseExpiry";
import { getApiErrorMessage } from "@/utils/redesignErrors";
import ImpersonationBanner from "@/components/layouts/ImpersonationBanner.vue";

export default {
  name: "license-selection",
  components: {
    ImpersonationBanner
  },
  data() {
    return {
      appTitle: process.env.VUE_APP_TITLE,
      logoText: process.env.VUE_APP_LOGO_TEXT,
      rows: [],
      totalRecords: 0,
      loading: true,
      selectingId: "",
      autoSelecting: false,
      errorMessage: ""
    };
  },
  computed: {
    ...mapGetters(["agentSession"]),
    memberSessionArr() {
      return this.$store.state.login.memberSession || {};
    },
    accountName() {
      return (
        this.memberSessionArr.name ||
        this.memberSessionArr.email ||
        "Akun Waajo"
      );
    },
    accountEmail() {
      return this.memberSessionArr.email || "";
    },
    accountInitials() {
      const source = this.accountName || "W";
      return source
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map(function(part) {
          return part.charAt(0).toUpperCase();
        })
        .join("");
    }
  },
  created() {
    if (this.hasActiveLicense()) {
      this.$router.replace("/copilot");
      return;
    }
    this.loadLicenses();
  },
  methods: {
    hasActiveLicense() {
      const session = this.agentSession || {};
      return Boolean(
        (session.license_phone_number &&
          String(session.license_phone_number).trim()) ||
          session.license_id
      );
    },
    async loadLicenses() {
      if (this.loading && this.rows.length) {
        return;
      }
      this.loading = true;
      this.errorMessage = "";
      this.autoSelecting = false;
      try {
        const response = await this.$store.dispatch("license/getLicenseList", {
          license_name: "",
          license_phone_number: "",
          license_status: "",
          page: 0,
          perPage: 1000
        });
        const payload = response && response.data ? response.data : {};
        this.rows = Array.isArray(payload.data) ? payload.data : [];
        this.totalRecords =
          typeof payload.totalRecords === "undefined"
            ? this.rows.length
            : Number(payload.totalRecords);

        if (
          this.totalRecords === 1 &&
          this.rows.length === 1 &&
          this.rows[0].id
        ) {
          this.autoSelecting = true;
          await this.selectLicense(this.rows[0], true);
        }
      } catch (error) {
        this.rows = [];
        this.totalRecords = 0;
        this.errorMessage = getApiErrorMessage(
          error,
          "Daftar lisensi belum dapat dimuat. Periksa koneksi lalu coba lagi."
        );
      } finally {
        this.loading = false;
        this.autoSelecting = false;
      }
    },
    async selectLicense(row, isAuto) {
      if (this.selectingId || !row || !row.id) {
        return;
      }
      this.selectingId = row.id;
      this.errorMessage = "";
      try {
        await this.$store.dispatch("agent/connectToAgentOwner", {
          license_id: row.id,
          agent_id: ""
        });
        if (this.$route.path !== "/") {
          this.$router.replace("/");
        }
      } catch (error) {
        this.errorMessage = getApiErrorMessage(
          error,
          "Lisensi belum dapat diaktifkan. Silakan coba lagi."
        );
        if (isAuto) {
          this.autoSelecting = false;
        }
      } finally {
        this.selectingId = "";
      }
    },
    statusFor(row) {
      if (Number(row.is_unlimited) === 1) {
        return { label: "Unlimited", className: "is-unlimited" };
      }
      if (Number(row.is_free) === 1) {
        return { label: "Free", className: "is-free" };
      }
      const label = String(row.status_label || "").toLowerCase();
      if (label === "active") {
        return { label: "Aktif", className: "is-active" };
      }
      if (label === "expired") {
        return { label: "Kedaluwarsa", className: "is-expired" };
      }
      return {
        label: row.status_label || "Tidak diketahui",
        className: "is-neutral"
      };
    },
    expiryLabel(row) {
      if (Number(row.is_unlimited) === 1) {
        return "Masa aktif tanpa batas";
      }
      if (!row.license_masa_aktif) {
        return "Tanggal masa aktif belum tersedia";
      }
      const formatted = formatLicenseDate(row.license_masa_aktif);
      if (!formatted) {
        return "Tanggal masa aktif tidak valid";
      }
      const expiry = getLicenseExpiryInfo(row.license_masa_aktif);
      if (expiry.state === "expired") {
        return "Kedaluwarsa sejak " + formatted;
      }
      return "Masa aktif hingga " + formatted;
    }
  }
};
</script>

<style scoped>
.waajo-license-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  background:
    radial-gradient(circle at 10% 96%, rgba(249, 154, 67, 0.13), transparent 30%),
    radial-gradient(circle at 95% 82%, rgba(249, 154, 67, 0.1), transparent 27%),
    #fbfcfe;
  color: #172033;
  font-family: "CircularStd", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
}

.waajo-license-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 76px;
  padding: 0 6vw;
  border-bottom: 1px solid rgba(231, 237, 244, 0.85);
  background: rgba(255, 255, 255, 0.84);
  backdrop-filter: blur(16px);
}

.waajo-license-brand {
  display: inline-flex;
  align-items: center;
  color: #f28d32;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.04em;
  text-decoration: none;
}

.waajo-license-brand:hover,
.waajo-license-brand:focus {
  color: #d97b22;
  text-decoration: none;
  outline: none;
}

.waajo-license-brand img {
  display: block;
  width: auto;
  max-width: 170px;
  height: 34px;
  object-fit: contain;
}

.waajo-license-account {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}

.waajo-license-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #5a4639;
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.waajo-license-account-copy {
  display: grid;
  gap: 1px;
  min-width: 0;
}

.waajo-license-account-copy strong {
  max-width: 220px;
  overflow: hidden;
  color: #3c4655;
  font-size: 12px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.waajo-license-account-copy small {
  max-width: 220px;
  overflow: hidden;
  color: #94a3b8;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.waajo-license-main {
  width: 100%;
  max-width: 1050px;
  margin: 0 auto;
  padding: 42px 22px 52px;
  flex: 1 1 auto;
}

.waajo-license-heading {
  max-width: 620px;
  margin: 0 auto 28px;
  text-align: center;
}

.waajo-license-heading-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  margin-bottom: 13px;
  border: 1px solid #fbe3cb;
  border-radius: 50%;
  background: #fff8f0;
  color: #f28d32;
  font-size: 27px;
}

.waajo-license-eyebrow {
  display: block;
  margin-bottom: 7px;
  color: #c46e1e;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.waajo-license-heading h1 {
  margin: 0;
  color: #172033;
  font-size: clamp(26px, 3.2vw, 36px);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.15;
}

.waajo-license-heading p {
  margin: 10px 0 0;
  color: #768397;
  font-size: 13px;
  line-height: 1.7;
}

.waajo-license-grid,
.waajo-license-loading {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 17px;
  max-width: 930px;
  margin: 0 auto;
}

.waajo-license-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 20px;
  border: 1px solid #e8edf3;
  border-radius: 17px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.055);
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
}

.waajo-license-card:hover {
  border-color: #f4d1ae;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.075);
  transform: translateY(-2px);
}

.waajo-license-card.is-selecting {
  border-color: #f1b271;
  box-shadow: 0 18px 40px rgba(249, 154, 67, 0.13);
}

.waajo-license-card-top {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.waajo-license-wa-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #eafaf0;
  color: #28a85a;
  font-size: 23px;
}

.waajo-license-card-title {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 2px;
}

.waajo-license-card-title h2 {
  margin: 0;
  overflow: hidden;
  color: #172033;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.waajo-license-card-title p {
  margin: 4px 0 0;
  overflow: hidden;
  color: #7c899a;
  font-size: 11px;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.waajo-license-detail {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  color: #8b98a9;
  text-decoration: none;
}

.waajo-license-detail:hover,
.waajo-license-detail:focus {
  background: #fff7ef;
  color: #c46e1e;
  text-decoration: none;
  outline: none;
}

.waajo-license-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 16px;
}

.waajo-license-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 10px;
  font-weight: 800;
  line-height: 1.3;
}

.waajo-license-status i {
  font-size: 6px;
}

.waajo-license-status.is-active {
  background: #ecfdf3;
  color: #15803d;
}

.waajo-license-status.is-expired {
  background: #fef2f2;
  color: #b91c1c;
}

.waajo-license-status.is-free {
  background: #eff6ff;
  color: #1d4ed8;
}

.waajo-license-status.is-unlimited {
  background: #f5f3ff;
  color: #6d28d9;
}

.waajo-license-online {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 700;
}

.waajo-license-online i {
  font-size: 9px;
}

.waajo-license-online.is-online {
  color: #15803d;
}

.waajo-license-expiry {
  margin: 11px 0 17px;
  color: #8b97a8;
  font-size: 10px;
  line-height: 1.45;
}

.waajo-license-primary,
.waajo-license-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 9px 14px;
  border: 1px solid transparent;
  border-radius: 9px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.2;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease,
    box-shadow 0.18s ease, transform 0.18s ease;
}

.waajo-license-primary {
  background: #f99a43;
  color: #ffffff;
  box-shadow: 0 8px 18px rgba(249, 154, 67, 0.2);
}

.waajo-license-primary:hover:not(:disabled),
.waajo-license-primary:focus:not(:disabled) {
  background: #ef8c32;
  color: #ffffff;
  text-decoration: none;
  outline: none;
  transform: translateY(-1px);
}

.waajo-license-primary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.waajo-license-secondary {
  border-color: #e8edf3;
  background: #ffffff;
  color: #58677a;
}

.waajo-license-secondary:hover,
.waajo-license-secondary:focus {
  border-color: #f5cfa8;
  background: #fffaf5;
  color: #b45309;
  text-decoration: none;
  outline: none;
}

.waajo-license-enter {
  width: 100%;
  margin-top: auto;
}

.waajo-license-state {
  max-width: 520px;
  margin: 0 auto;
  padding: 42px 24px;
  border: 1px solid #e8edf3;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.05);
  text-align: center;
}

.waajo-license-state-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  margin-bottom: 13px;
  border-radius: 50%;
  background: #fff3e6;
  color: #e88931;
  font-size: 23px;
}

.waajo-license-state-icon.is-danger {
  background: #fef2f2;
  color: #dc2626;
}

.waajo-license-state h2 {
  margin: 0;
  color: #172033;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.025em;
}

.waajo-license-state p {
  margin: 8px 0 19px;
  color: #7b8798;
  font-size: 12px;
  line-height: 1.65;
}

.waajo-license-state-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  flex-wrap: wrap;
}

.waajo-license-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 930px;
  margin: 0 auto 14px;
  padding: 10px 12px;
  border: 1px solid #fecaca;
  border-radius: 10px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 11px;
  line-height: 1.5;
}

.waajo-license-loading {
  max-width: 930px;
}

.waajo-license-skeleton {
  min-height: 186px;
  gap: 10px;
  pointer-events: none;
}

.waajo-license-skeleton:hover {
  transform: none;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.055);
}

.waajo-skeleton-icon,
.waajo-skeleton-title,
.waajo-skeleton-subtitle,
.waajo-skeleton-pill,
.waajo-skeleton-action {
  display: block;
  border-radius: 8px;
  background: linear-gradient(90deg, #f5f7fa 25%, #eef2f6 50%, #f5f7fa 75%);
  background-size: 200% 100%;
  animation: waajo-license-shimmer 1.4s infinite;
}

.waajo-skeleton-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
}

.waajo-skeleton-title {
  width: 65%;
  height: 14px;
  margin-top: 2px;
}

.waajo-skeleton-subtitle {
  width: 42%;
  height: 9px;
}

.waajo-skeleton-pill {
  width: 64px;
  height: 20px;
  margin-top: 7px;
  border-radius: 999px;
}

.waajo-skeleton-action {
  width: 100%;
  height: 40px;
  margin-top: auto;
}

@keyframes waajo-license-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.waajo-license-loading-text {
  grid-column: 1 / -1;
  margin: 2px 0 0;
  color: #8b97a8;
  font-size: 11px;
  text-align: center;
}

.waajo-license-help {
  margin: 20px 0 0;
  color: #94a3b8;
  font-size: 10px;
  text-align: center;
}

.waajo-license-help a {
  color: #c46e1e;
  font-weight: 800;
  text-decoration: none;
}

.waajo-license-help a:hover,
.waajo-license-help a:focus {
  color: #92400e;
  text-decoration: underline;
  outline: none;
}

.waajo-license-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
  max-width: 1050px;
  margin: 0 auto;
  padding: 0 22px 24px;
  color: #94a3b8;
  font-size: 10px;
}

.waajo-license-footer a {
  color: #b45309;
  font-weight: 700;
  text-decoration: none;
}

.waajo-license-footer a:hover,
.waajo-license-footer a:focus {
  color: #92400e;
  text-decoration: underline;
  outline: none;
}

.waajo-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: waajo-license-spin 0.7s linear infinite;
}

@keyframes waajo-license-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 767.98px) {
  .waajo-license-header {
    min-height: 68px;
    padding: 0 18px;
  }

  .waajo-license-brand img {
    max-width: 140px;
    height: 29px;
  }

  .waajo-license-account-copy small {
    display: none;
  }

  .waajo-license-main {
    padding: 30px 15px 40px;
  }

  .waajo-license-heading {
    margin-bottom: 22px;
  }

  .waajo-license-heading-icon {
    width: 52px;
    height: 52px;
    font-size: 23px;
  }

  .waajo-license-grid,
  .waajo-license-loading {
    grid-template-columns: 1fr;
    max-width: 430px;
  }

  .waajo-license-footer {
    padding: 0 15px 20px;
  }
}

@media (max-width: 420px) {
  .waajo-license-account-copy {
    max-width: 120px;
  }

  .waajo-license-account-copy strong {
    max-width: 120px;
  }

  .waajo-license-card {
    padding: 17px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/dashboard/index.vue